import { useEffect, useRef, useState } from 'react';
import { Check, LoaderCircle } from 'lucide-react';
import { getTurnstileSiteKey, getTurnstileToken } from '@/lib/turnstile';

type Props = {
  onToken: (token: string) => void;
  onExpire: () => void;
  /** When no site key is configured, call once so submit can proceed locally. */
  onSkip?: () => void;
};

/**
 * Custom checkbox that triggers an invisible Turnstile challenge on click.
 * The Cloudflare widget itself stays hidden; only this control is shown.
 */
export function ContactTurnstile({ onToken, onExpire, onSkip }: Props) {
  const [checked, setChecked] = useState(false);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState(false);
  const [available, setAvailable] = useState(true);
  const busyRef = useRef(false);
  const onTokenRef = useRef(onToken);
  const onExpireRef = useRef(onExpire);
  const onSkipRef = useRef(onSkip);
  onTokenRef.current = onToken;
  onExpireRef.current = onExpire;
  onSkipRef.current = onSkip;

  useEffect(() => {
    let cancelled = false;
    void getTurnstileSiteKey().then((key) => {
      if (cancelled) return;
      if (!key) {
        setAvailable(false);
        onSkipRef.current?.();
      }
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const reset = () => {
    setChecked(false);
    setPending(false);
    setError(false);
    busyRef.current = false;
    onExpireRef.current();
  };

  const verify = async () => {
    if (busyRef.current || pending || checked) return;
    if (!available) {
      setChecked(true);
      onTokenRef.current('');
      return;
    }

    busyRef.current = true;
    setPending(true);
    setError(false);
    onExpireRef.current();

    try {
      const token = await getTurnstileToken('contact');
      setChecked(true);
      setPending(false);
      setError(false);
      onTokenRef.current(token);
    } catch {
      setChecked(false);
      setPending(false);
      setError(true);
      onExpireRef.current();
    } finally {
      busyRef.current = false;
    }
  };

  const onToggle = () => {
    if (pending) return;
    if (checked) {
      reset();
      return;
    }
    void verify();
  };

  return (
    <button
      type="button"
      className={`contact-captcha${checked ? ' is-checked' : ''}${pending ? ' is-pending' : ''}${error ? ' is-error' : ''}`}
      onClick={onToggle}
      aria-pressed={checked}
      aria-busy={pending}
      disabled={pending}
    >
      <span className="contact-captcha-box" aria-hidden>
        {pending ? (
          <LoaderCircle size={14} strokeWidth={2.5} className="contact-captcha-spin" />
        ) : checked ? (
          <Check size={14} strokeWidth={2.5} />
        ) : null}
      </span>
      <span className="contact-captcha-text">
        {pending
          ? 'Verifying…'
          : error
            ? 'Try again'
            : checked
              ? 'Verified'
              : "I'm not a robot"}
      </span>
    </button>
  );
}
